<div class="pb-hero">
  <p class="pb-eyebrow">Pythrust Product Builders</p>
  <h1 class="pb-headline">
    When your idea needs more than <span class="pb-accent">AI</span> can give it
  </h1>
  <p class="pb-subhead">
    A Product Builder is a Pythrust expert — someone who understands both code and
    design — who works with you directly. You get full access the entire time, and
    the approach is custom to your case, whether that means building on Angularize
    or going fully bespoke.
  </p>
  <div class="pb-hero-actions">
    <button type="button" class="pb-btn pb-btn--primary" (click)="scrollToForm()">
      Talk to a Product Builder
    </button>
    <button type="button" class="pb-btn pb-btn--ghost" (click)="scrollToHowItWorks()">
      See how it works
    </button>
  </div>
</div>

<div class="pb-section" id="how-it-works">
  <p class="pb-section-eyebrow">How it works</p>
  <h2 class="pb-section-title">Three steps, at your pace</h2>

  <div class="pb-steps">
    <div class="pb-step">
      <div class="pb-step-marker">
        <span class="pb-step-number">1</span>
      </div>
      <h3 class="pb-step-title">Ideate on Angularize</h3>
      <p class="pb-step-body">
        Optional, but useful — sketch your idea with AI first so your Product
        Builder starts from something real, not a blank page.
      </p>
    </div>
    <div class="pb-step-connector" aria-hidden="true"></div>
    <div class="pb-step">
      <div class="pb-step-marker">
        <span class="pb-step-number">2</span>
      </div>
      <h3 class="pb-step-title">Meet your Product Builder</h3>
      <p class="pb-step-body">
        Share your requirements below. A Pythrust expert reviews your case and
        reaches out to understand what you actually need.
      </p>
    </div>
    <div class="pb-step-connector" aria-hidden="true"></div>
    <div class="pb-step">
      <div class="pb-step-marker">
        <span class="pb-step-number">3</span>
      </div>
      <h3 class="pb-step-title">Build together</h3>
      <p class="pb-step-body">
        Full access, the whole way through. Whatever tools make sense for your
        case — Angularize or fully custom — you're never in the dark about it.
      </p>
    </div>
  </div>
</div>

<div class="pb-section pb-section--muted">
  <p class="pb-section-eyebrow">What a Product Builder brings</p>
  <h2 class="pb-section-title">One person, both sides of the work</h2>

  <div class="pb-traits">
    <div class="pb-trait">
      <mat-icon class="pb-trait-icon">code</mat-icon>
      <h3 class="pb-trait-title">Code and design, together</h3>
      <p class="pb-trait-body">
        No handoff gap between "how it looks" and "how it works" — one person
        owns both, so nothing gets lost in translation.
      </p>
    </div>
    <div class="pb-trait">
      <mat-icon class="pb-trait-icon">visibility</mat-icon>
      <h3 class="pb-trait-title">Full access, always</h3>
      <p class="pb-trait-body">
        You see the work as it happens. No black box, no surprise reveal at
        the end.
      </p>
    </div>
    <div class="pb-trait">
      <mat-icon class="pb-trait-icon">tune</mat-icon>
      <h3 class="pb-trait-title">Approach fits your case</h3>
      <p class="pb-trait-body">
        Some projects fit Angularize well. Others need something fully
        custom. Your Product Builder figures out which, with you.
      </p>
    </div>
  </div>
</div>

<div class="pb-section" id="product-builder-form">
  <div class="pb-form-wrapper">
    <ng-container *ngIf="!submitted; else successState">
      <p class="pb-section-eyebrow">Get started</p>
      <h2 class="pb-section-title">Tell us what you're building</h2>
      <p class="pb-form-subhead">
        A quick note on scope, budget range, and timeline helps us match you
        with the right Product Builder.
      </p>

      <form class="pb-form" (ngSubmit)="submitRequest()" #pbForm="ngForm">
        <div class="pb-field-row">
          <div class="pb-field">
            <label for="pb-name">Your name</label>
            <input
              id="pb-name"
              type="text"
              name="name"
              [(ngModel)]="form.name"
              placeholder="Ankit Singh"
              autocomplete="name"
            />
          </div>
          <div class="pb-field">
            <label for="pb-email">Email</label>
            <input
              id="pb-email"
              type="email"
              name="email"
              [(ngModel)]="form.email"
              placeholder="you@company.com"
              autocomplete="email"
            />
          </div>
        </div>

        <div class="pb-field">
          <label for="pb-description">What do you want to build?</label>
          <textarea
            id="pb-description"
            name="projectDescription"
            [(ngModel)]="form.projectDescription"
            rows="5"
            placeholder="Tell us about your idea, your users, and what success looks like for this project."
          ></textarea>
        </div>

        <div class="pb-field-row">
          <div class="pb-field">
            <label for="pb-budget">Budget range</label>
            <select id="pb-budget" name="budgetRange" [(ngModel)]="form.budgetRange">
              <option value="" disabled selected>Select a range</option>
              <option *ngFor="let opt of budgetOptions" [value]="opt">{{ opt }}</option>
            </select>
          </div>
          <div class="pb-field">
            <label for="pb-timeline">Timeline</label>
            <select id="pb-timeline" name="timeline" [(ngModel)]="form.timeline">
              <option value="" disabled selected>Select a timeline</option>
              <option *ngFor="let opt of timelineOptions" [value]="opt">{{ opt }}</option>
            </select>
          </div>
        </div>

        <p class="pb-error" *ngIf="errorMessage">{{ errorMessage }}</p>

        <button type="submit" class="pb-btn pb-btn--primary pb-btn--wide" [disabled]="submitting">
          <span *ngIf="!submitting">Send my request</span>
          <span *ngIf="submitting">Sending...</span>
        </button>
      </form>
    </ng-container>

    <ng-template #successState>
      <div class="pb-success">
        <mat-icon class="pb-success-icon">check_circle</mat-icon>
        <h2 class="pb-section-title">Request sent</h2>
        <p class="pb-form-subhead">
          Thanks, {{ form.name }}. A Product Builder will review your request
          and reach out at {{ form.email }} shortly.
        </p>
        <button type="button" class="pb-btn pb-btn--ghost" (click)="submitAnother()">
          Send another request
        </button>
      </div>
    </ng-template>
  </div>
</div>